import React, { createContext, useContext, useMemo } from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { useAuthContext } from 'AuthContext';
import MeetingService from 'services/MeetingService';
import { useMeetingHub } from 'hooks/useMeetingHub';
import useRequest from 'hooks/useRequest';
import { ActivityLog, Attendee, MeetingSession } from 'store/types/Meeting';

export interface MeetingHubContextType {
  meetingId: string;
  attendeeId: string;
  isPresidingChair: boolean;
  canViewAllVoters: boolean;

  connection: HubConnection | null;
  isConnected: boolean;
  callHubMethod: (method: string, payload?: any) => Promise<void>;

  attendees: Attendee[];
  loadingAttendees?: boolean;
  refetchAttendees?: () => void;

  meetingSession?: MeetingSession;
  loadingMeetingSession?: boolean;
  refetchMeetingSession?: () => void;

  activityLogs: ActivityLog[];
  loadingActivityLogs?: boolean;
  refetchActivityLogs?: () => void;
}

const MeetingHubContext = createContext<MeetingHubContextType>({} as MeetingHubContextType);

interface MeetingHubProviderProps {
  meetingId: string;
  attendeeId: string;
  isPreview?: boolean;
  isLog?: boolean;
}

export const MeetingHubProvider: React.FunctionComponent<MeetingHubProviderProps> = ({
  meetingId,
  attendeeId,
  isPreview = false,
  isLog = false,
  children,
}) => {
  const { isStaff, profile } = useAuthContext();
  const attendeeName = `${profile?.customer?.firstname ?? ''} ${profile?.customer?.lastname ?? ''}`.trim();
  const attendeeCompany =
    [
      profile?.customer?.companyName,
      profile?.customer?.memberCompanyName,
      profile?.customer?.localCompanyName,
      profile?.customer?.memberLocalCompanyName,
      profile?.customer?.phoneticCompanyName,
      profile?.customer?.memberPhoneticCompanyName,
    ]
      .map((value) => value?.trim())
      .find((value) => value) ?? undefined;
  const { connection, isConnected, callHubMethod } = useMeetingHub(
    meetingId,
    attendeeId,
    attendeeName,
    attendeeCompany,
    isPreview,
    isLog
  );

  const getMeetingSessionRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getMeetingSession(meetingId)),
    [meetingId]
  );
  const {
    data: meetingSession,
    loading: loadingMeetingSession,
    refetch: refetchMeetingSession,
  } = useRequest<MeetingSession>(getMeetingSessionRequest);

  const getAttendeesRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getAttendees(meetingId)),
    [meetingId]
  );
  const {
    data: attendees = [],
    firstLoading: loadingAttendees,
    refetch: refetchAttendees,
  } = useRequest<Attendee[]>(getAttendeesRequest);

  const isPresidingChair = useMemo(
    () => attendeeId === meetingSession?.presidingChairId,
    [attendeeId, meetingSession?.presidingChairId]
  );

  const getActivityLogsRequest = useMemo(
    () => (!meetingId ? undefined : () => MeetingService.getActivityLogs(meetingId)),
    [meetingId]
  );
  const {
    data: activityLogs = [],
    firstLoading: loadingActivityLogs,
    refetch: refetchActivityLogs,
  } = useRequest<ActivityLog[]>(getActivityLogsRequest);

  const canViewAllVoters = useMemo(() => isStaff || isPresidingChair, [isPresidingChair, isStaff]);

  const context: MeetingHubContextType = {
    meetingId,
    attendeeId,
    isPresidingChair,
    canViewAllVoters,

    connection,
    isConnected,
    callHubMethod,

    attendees,
    loadingAttendees,
    refetchAttendees,

    meetingSession,
    loadingMeetingSession,
    refetchMeetingSession,

    activityLogs,
    loadingActivityLogs,
    refetchActivityLogs,
  };

  return <MeetingHubContext.Provider value={context}>{children}</MeetingHubContext.Provider>;
};

export default MeetingHubProvider;

export const useMeetingHubContext = (): MeetingHubContextType => {
  const context = useContext(MeetingHubContext);

  if (context === undefined) {
    throw new Error('MeetingHubContext was used outside of its Provider');
  }

  return context;
};
